import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { 
  AlertTriangle, 
  Search, 
  Ticket,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  RefreshCw,
  AlertCircle,
  Eye,
  Pen,
  Paperclip,
  Filter
} from 'lucide-react';
import { ticketService } from '../api/ticketService';
import { usePriorities } from '../hooks/usePriorities';
import { useStatuses } from '../hooks/useStatuses';
import { toast } from 'react-hot-toast';
import {
  resolveTicketCategoryLabel,
  resolveTicketPriorityLabel,
  resolveInquiryBreadcrumb,
  resolveCurrentSlaWindow,
} from '../utils/inquiryDisplay';

/**
 * EscalatedTicketsTable - Reusable component for displaying escalated tickets in a table format
 * 
 * @param {Function} fetchFunction - Function that accepts (page, size) and returns a promise with ticket data
 * @param {Function} onViewTicket - Optional callback when viewing a ticket (receives ticketId)
 * @param {Function} onEditTicket - Optional callback when editing a ticket (receives ticketId)
 * @param {String} viewTicketRoute - Optional route pattern for viewing tickets (e.g., '/agent/tickets/:id')
 * @param {Object} additionalQueryParams - Optional additional query parameters to pass to fetchFunction
 * @param {Boolean} showStatistics - Whether to show statistics cards (default: false)
 * @param {String} emptyStateMessage - Custom message when no tickets found
 * @param {Function} onStatisticsUpdate - Optional callback to receive statistics updates (receives stats object)
 * @param {Array} externalPriorities - When provided, skips loading priorities in the table (parent loads once)
 * @param {Boolean} showFilters - Whether to show filter section (controlled by parent)
 * @param {Number} refreshTrigger - When this value changes, the table will refresh
 */
const EscalatedTicketsTable = ({
  fetchFunction,
  onViewTicket,
  onEditTicket,
  viewTicketRoute,
  additionalQueryParams = {},
  showStatistics = false,
  emptyStateMessage = 'No tickets are currently escalated',
  onStatisticsUpdate,
  externalPriorities,
  showFilters: externalShowFilters,
  refreshTrigger,
  searchTerm: externalSearchTerm
}) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false); // Separate loading state for search operations
  const [error, setError] = useState('');
  const [internalShowFilters, setInternalShowFilters] = useState(false);
  // Use external showFilters if provided, otherwise use internal state
  const showFilters = externalShowFilters !== undefined ? externalShowFilters : internalShowFilters;
  // Use external searchTerm if provided, otherwise use internal state
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('escalated');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState({
    pageNumber: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false
  });
  const parentProvidesPriorities = externalPriorities !== undefined;
  const { data: statuses = [] } = useStatuses();
  const { data: cachedPriorities = [] } = usePriorities({ enabled: !parentProvidesPriorities });
  const priorities = parentProvidesPriorities ? externalPriorities : cachedPriorities;
  const [statistics, setStatistics] = useState({
    totalEscalated: 0,
    criticalEscalated: 0,
    highEscalated: 0
  });
  const [slaTickSeconds, setSlaTickSeconds] = useState(0);
  const ticketsSlaFetchedAtRef = useRef(Date.now());
  const hasBootstrappedRef = useRef(false);
  const skipInitialSearchLoadRef = useRef(true);
  const skipPageEffectAfterBootstrapRef = useRef(false);
  const lastRefreshTriggerRef = useRef(refreshTrigger);

  // Bootstrap: single initial fetch once priorities are available
  useEffect(() => {
    if (priorities.length === 0 || hasBootstrappedRef.current) return;
    hasBootstrappedRef.current = true;
    skipPageEffectAfterBootstrapRef.current = true;
    loadTickets(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorities]);

  // Pagination after bootstrap (skip the page-0 pass that coincides with bootstrap)
  useEffect(() => {
    if (!hasBootstrappedRef.current || priorities.length === 0) return;
    if (skipPageEffectAfterBootstrapRef.current) {
      skipPageEffectAfterBootstrapRef.current = false;
      return;
    }
    loadTickets(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Refresh when parent filter changes bump refreshTrigger (skip the mount-time bump)
  useEffect(() => {
    if (!hasBootstrappedRef.current || priorities.length === 0) return;
    if (refreshTrigger === undefined || refreshTrigger === lastRefreshTriggerRef.current) {
      return;
    }
    lastRefreshTriggerRef.current = refreshTrigger;
    if (refreshTrigger <= 0) return;

    if (currentPage !== 0) {
      setCurrentPage(0);
      return;
    }
    loadTickets(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Server-side search with debounce (skip empty search on initial mount)
  useEffect(() => {
    if (!hasBootstrappedRef.current || priorities.length === 0) return undefined;

    if (skipInitialSearchLoadRef.current) {
      skipInitialSearchLoadRef.current = false;
      if (!searchTerm?.trim()) {
        return undefined;
      }
    }

    if (currentPage !== 0) {
      setCurrentPage(0);
      return undefined;
    }

    const searchTimeout = setTimeout(() => {
      loadTickets(true);
    }, searchTerm.trim() ? 500 : 300);

    return () => clearTimeout(searchTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Client-side sorting only (server handles filtering)
  useEffect(() => {
    if (tickets.length > 0) {
      let sorted = [...tickets];
      
      // Sort tickets
      sorted.sort((a, b) => {
        switch (sortBy) {
          case 'escalated':
            return new Date(b.escalationDate || b.updatedAt) - new Date(a.escalationDate || a.updatedAt);
          case 'created':
            return new Date(b.createdAt) - new Date(a.createdAt);
          case 'updated':
            return new Date(b.updatedAt) - new Date(a.updatedAt);
          case 'priority':
            const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
            return priorityOrder[b.priority] - priorityOrder[a.priority];
          default:
            return 0;
        }
      });
      
      setFilteredTickets(sorted);
    }
  }, [tickets, sortBy]);

  useEffect(() => {
    if (!tickets?.length) return undefined;
    const interval = setInterval(() => {
      setSlaTickSeconds(Math.floor((Date.now() - ticketsSlaFetchedAtRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [tickets]);

  const loadTickets = async (isSearchOperation = false) => {
    if (!fetchFunction) {
      console.error('fetchFunction is required for EscalatedTicketsTable');
      setError('Fetch function not provided');
      return;
    }

    // Use searchLoading for search operations to avoid full page reload
    if (isSearchOperation) {
      setSearchLoading(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      const availablePriorities = priorities;

      // Call the provided fetch function with pagination and additional params
      // searchTerm is already included in fetchFunction via parent component
      const params = {
        page: currentPage,
        size: pageSize,
        ...additionalQueryParams
      };
      
      const response = await fetchFunction(params);
      
      // Backend returns ResponseBean: { status, message, data, success, timestamp }
      // data is PaginatedResponse with content, pageNumber, pageSize, totalElements, etc.
      const responseData = response?.data || response;
      
      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        const backendTickets = paginatedData.content || [];
        
        // Transform backend tickets to frontend format
        const transformedTickets = backendTickets
          .filter(ticket => {
            const status = ticket.status?.toLowerCase() || '';
            // Exclude closed and resolved tickets
            return status !== 'closed' && status !== 'resolved';
          })
          .map(ticket => {
            const priorityLabel = resolveTicketPriorityLabel(ticket);
            let priority = (priorityLabel !== '—' ? priorityLabel : 'medium').toLowerCase();
            let priorityName = priorityLabel !== '—' ? priorityLabel : 'Medium';
            let priorityId = ticket.priorityId;

            if (priorityLabel === '—' && priorityId && availablePriorities.length > 0) {
              const priorityObj = availablePriorities.find(p => p.priorityId === priorityId || p.priorityId?.toString() === priorityId?.toString());
              if (priorityObj) {
                priority = (priorityObj.priorityCode || priorityObj.priorityName || 'medium').toLowerCase();
                priorityName = priorityObj.priorityName || priorityObj.priorityCode || 'Medium';
              }
            } else if (priorityLabel === '—' && ticket.priority) {
              priority = String(ticket.priority).toLowerCase();
              priorityName = ticket.priority;
            }

            const slaWindow = resolveCurrentSlaWindow(ticket, 0, statuses);
            const category = resolveTicketCategoryLabel(ticket);
            const breadcrumb = resolveInquiryBreadcrumb(ticket);
            
            return {
              id: ticket.ticketId || ticket.ticketNumber,
              ticketNumber: ticket.ticketNumber,
              title: ticket.subject,
              description: ticket.description || '',
              status: ticket.status?.toLowerCase() || 'escalated',
              priority: priority,
              priorityName: priorityName,
              priorityId: priorityId,
              category,
              categoryId: ticket.categoryId,
              inquiryTypeName: ticket.inquiryTypeName,
              inquiryCategoryName: ticket.inquiryCategoryName,
              inquirySubCategoryName: ticket.inquirySubCategoryName,
              inquiryPriorityLevelName: ticket.inquiryPriorityLevelName,
              inquiryPriorityLevelId: ticket.inquiryPriorityLevelId,
              inquiryBreadcrumb: breadcrumb,
              responsibleRoleName: ticket.responsibleRoleName,
              escalationLevelName: ticket.escalationLevelName,
              customer: {
                name: ticket.requesterEmail ? ticket.requesterEmail.split('@')[0] : 'Unknown',
                email: ticket.requesterEmail || '',
                company: ''
              },
              assignedAgent: ticket.assigneeFullName || (ticket.assigneeId ? `Agent ${ticket.assigneeId}` : 'Unassigned'),
              assigneeId: ticket.assigneeId,
              assignedTime: ticket.assignedTime,
              createdAt: ticket.createdAt,
              updatedAt: ticket.updatedAt || ticket.createdAt,
              escalationDate: ticket.escalatedAt || ticket.escalatedDate || ticket.updatedAt || ticket.createdAt,
              dueDate: ticket.resolvedAt || ticket.updatedAt,
              tags: [],
              attachments: ticket.attachmentCount || 0,
              comments: ticket.replyCount || 0,
              slaStatus: slaWindow?.isOverdue ? 'breached' : 'on-track',
              slaLabel: slaWindow?.label || '—',
              slaIsOverdue: !!slaWindow?.isOverdue,
              slaRemainingMinutes: ticket.slaRemainingMinutes,
              slaRemainingSeconds: ticket.slaRemainingSeconds,
              slaOverdueMinutes: ticket.slaOverdueMinutes,
              slaOverdueSeconds: ticket.slaOverdueSeconds,
              slaElapsedMinutes: ticket.slaElapsedMinutes,
              slaIsBreached: ticket.slaIsBreached,
              slaIsEscalated: ticket.slaIsEscalated,
              timeSpent: '0h 0m',
              estimatedTime: '0h',
              lastActivity: ticket.updatedAt || ticket.createdAt,
              countryId: ticket.countryId,
              countyId: ticket.countyId,
              subCountyId: ticket.subCountyId,
              wardId: ticket.wardId,
              countryName: ticket.countryName,
              countyName: ticket.countyName,
              subCountyName: ticket.subCountyName,
              wardName: ticket.wardName
            };
          });
        
        ticketsSlaFetchedAtRef.current = Date.now();
        setSlaTickSeconds(0);
        setTickets(transformedTickets);
        setFilteredTickets(transformedTickets);
        
        // Store pagination data
        setPaginationData({
          pageNumber: paginatedData.pageNumber || currentPage,
          pageSize: paginatedData.pageSize || pageSize,
          totalElements: paginatedData.totalElements || 0,
          totalPages: paginatedData.totalPages || 0,
          first: paginatedData.first !== undefined ? paginatedData.first : (currentPage === 0),
          last: paginatedData.last !== undefined ? paginatedData.last : false
        });
        
        // Calculate statistics if needed or if callback is provided
        if (showStatistics || onStatisticsUpdate) {
          const stats = calculateStatistics(transformedTickets);
          if (showStatistics) {
            setStatistics(stats);
          }
        }
      } else {
        setError(responseData?.message || 'Failed to load escalated tickets');
        setTickets([]);
        setFilteredTickets([]);
      }
    } catch (err) {
      console.error('Error loading escalated tickets:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load escalated tickets';
      setError(errorMessage);
      toast.error(errorMessage);
      setTickets([]);
      setFilteredTickets([]);
    } finally {
      // Reset appropriate loading state
      if (isSearchOperation) {
        setSearchLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const calculateStatistics = (tickets) => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    const thisWeekStart = new Date(todayStart);
    thisWeekStart.setDate(todayStart.getDate() - todayStart.getDay());
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);

    let criticalCount = 0;
    let highCount = 0;
    let mediumCount = 0;
    let lowCount = 0;
    let todayCount = 0;
    let yesterdayCount = 0;
    let thisWeekCount = 0;
    let lastWeekCount = 0;
    let totalEscalationMinutes = 0;
    let escalationCount = 0;
    let slaBreachRisk = 0;

    tickets.forEach(ticket => {
      // Priority counts
      if (ticket.priority === 'critical') criticalCount++;
      else if (ticket.priority === 'high') highCount++;
      else if (ticket.priority === 'medium') mediumCount++;
      else if (ticket.priority === 'low') lowCount++;

      // Escalation trends
      const escalatedDate = new Date(ticket.escalationDate || ticket.updatedAt || ticket.createdAt);
      if (escalatedDate >= todayStart) todayCount++;
      else if (escalatedDate >= yesterdayStart && escalatedDate < todayStart) yesterdayCount++;
      
      if (escalatedDate >= thisWeekStart) thisWeekCount++;
      else if (escalatedDate >= lastWeekStart && escalatedDate < thisWeekStart) lastWeekCount++;

      // Average escalation time
      if (ticket.createdAt && ticket.escalationDate) {
        const created = new Date(ticket.createdAt);
        const escalated = new Date(ticket.escalationDate);
        const diffMinutes = Math.floor((escalated - created) / (1000 * 60));
        if (diffMinutes > 0) {
          totalEscalationMinutes += diffMinutes;
          escalationCount++;
        }
      }

      // SLA breach risk — current window overdue only
      if (ticket.slaIsOverdue || ticket.slaStatus === 'breached') {
        slaBreachRisk++;
      }
    });

    let avgEscalationTime = '0h 0m';
    if (escalationCount > 0) {
      const avgMinutes = totalEscalationMinutes / escalationCount;
      const hours = Math.floor(avgMinutes / 60);
      const minutes = Math.floor(avgMinutes % 60);
      avgEscalationTime = `${hours}h ${minutes}m`;
    }

    const stats = {
      totalEscalated: tickets.length,
      criticalEscalated: criticalCount,
      highEscalated: highCount,
      mediumEscalated: mediumCount,
      lowEscalated: lowCount,
      avgEscalationTime: avgEscalationTime,
      escalationRate: 0, // Would need total tickets to calculate
      slaBreachRisk: slaBreachRisk,
      escalationTrends: {
        today: todayCount,
        yesterday: yesterdayCount,
        thisWeek: thisWeekCount,
        lastWeek: lastWeekCount
      }
    };

    // Call callback if provided
    if (onStatisticsUpdate) {
      onStatisticsUpdate(stats);
    }

    return stats;
  };


  const handleViewTicket = (ticketId) => {
    if (onViewTicket) {
      onViewTicket(ticketId);
    } else if (viewTicketRoute) {
      // Navigate with view mode (read-only) like TeamTickets
      navigate(viewTicketRoute.replace(':id', ticketId), { 
        state: { mode: 'view', openComments: true, scrollToComments: true } 
      });
    } else {
      // Default route with view mode
      navigate(`/agent/tickets/${ticketId}`, { 
        state: { mode: 'view', openComments: true, scrollToComments: true } 
      });
    }
  };

  const handleEditTicket = (ticketId) => {
    if (onEditTicket) {
      onEditTicket(ticketId);
    } else if (viewTicketRoute) {
      // Navigate without view mode restriction - allows editing
      navigate(viewTicketRoute.replace(':id', ticketId), { 
        state: { mode: 'edit' } 
      });
    } else {
      // Default route with edit mode
      navigate(`/agent/tickets/${ticketId}`, { 
        state: { mode: 'edit' } 
      });
    }
  };

  const handleRefresh = () => {
    loadTickets();
  };

  const handleToggleFilters = () => {
    // Only update internal state if external control is not provided
    if (externalShowFilters === undefined) {
      setInternalShowFilters(!internalShowFilters);
    }
  };

  const handleCloseFilters = () => {
    // Only update internal state if external control is not provided
    // If external control is provided, the parent component should handle closing
    if (externalShowFilters === undefined) {
      setInternalShowFilters(false);
    }
  };

  const handleClearFilters = () => {
    // Only clear internal searchTerm if not using external searchTerm
    if (externalSearchTerm === undefined) {
      setInternalSearchTerm('');
    }
    setPriorityFilter('all');
    toast.success('All filters cleared');
  };

  // Helper functions
  const getStatusIcon = (status) => {
    switch (status) {
      case 'escalated':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Ticket className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'escalated': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900';
      case 'open': return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900';
      case 'in-progress': 
      case 'in_progress': return 'text-white dark:text-white bg-teal-600 dark:bg-teal-700';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getSLAStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case 'on-track':
        return 'text-green-600';
      case 'at-risk':
        return 'text-yellow-600';
      case 'breached':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900';
      case 'urgent': return 'text-red-50 dark:text-red-100 bg-red-800 dark:bg-red-950';
      case 'high': return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
      case 'low': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getPriorityBackgroundColor = (priority) => {
    return 'bg-white dark:bg-gray-800';
  };

  const getPriorityBorderColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'border-l-4 border-green-400';
      case 'medium': return 'border-l-4 border-yellow-400';
      case 'high': return 'border-l-4 border-orange-400';
      case 'urgent': return 'border-l-4 border-red-700 dark:border-red-600';
      case 'critical': return 'border-l-4 border-red-400';
      default: return 'border-l-4 border-gray-300 dark:border-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="w-4 h-4" />
          <div>
            <h4 className="font-semibold">Error loading escalated tickets</h4>
            <p className="text-sm">{error}</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadTickets}
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading escalated tickets...</p>
          </CardContent>
        </Card>
      )}

      {/* Filters - Only show if searchTerm is not provided externally (parent handles search) */}
      {showFilters && externalSearchTerm === undefined && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-nowrap">
              {/* Search Input - Only show if not provided externally */}
              {externalSearchTerm === undefined && (
                <div className="relative flex-1 min-w-0">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tickets..."
                    value={internalSearchTerm}
                    onChange={(e) => {
                      e.preventDefault();
                      setInternalSearchTerm(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-full"
                  />
                </div>
              )}

              {/* Priority Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                >
                  <option value="all">All Priority</option>
                  {priorities
                    .filter((priority) => priority.priorityId != null)
                    .map((priority) => {
                      const priorityCode = (priority.priorityCode || priority.priorityName || '').toLowerCase();
                      return (
                        <option key={priority.priorityId || priorityCode} value={priorityCode}>
                          {priority.priorityName || priority.priorityCode || priorityCode}
                        </option>
                      );
                    })}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Sort By Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[160px]"
                >
                  <option value="escalated">Escalation Date</option>
                  <option value="updated">Last Updated</option>
                  <option value="created">Created Date</option>
                  <option value="priority">Priority</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-sm px-4 py-2 whitespace-nowrap"
                >
                  Clear All
                </Button>
                <Button 
                  size="sm"
                  onClick={handleCloseFilters}
                  className="text-sm px-4 py-2 whitespace-nowrap"
                >
                  Apply
                </Button>
                <button
                  onClick={handleCloseFilters}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tickets List */}
      {!loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden relative">
          {searchLoading && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-blue-600 animate-pulse z-10"></div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Escalation Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Responsible Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Assignee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    SLA
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Escalated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTickets.map((ticket, index) => {
                  // Calculate serial number based on current page
                  const serialNumber = currentPage * pageSize + index + 1;
                  const priority = ticket.priority || 'medium';
                  const bgColor = getPriorityBackgroundColor(priority);
                  const borderColor = getPriorityBorderColor(priority);
                  const liveSla = resolveCurrentSlaWindow(ticket, slaTickSeconds, statuses);
                  const slaLabel = liveSla?.label || ticket.slaLabel || '—';
                  const slaOverdue = liveSla?.isOverdue ?? ticket.slaIsOverdue;
                  
                  // All escalated tickets should have red highlighting
                  const isEscalated = ticket.status === 'escalated' || ticket.slaIsEscalated === true;
                  
                  return (
                    <tr 
                      key={ticket.id} 
                      className={`${isEscalated ? 'bg-red-50 dark:bg-red-900/20' : bgColor} ${borderColor} hover:shadow-md transition-all`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {serialNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                              <Ticket className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {ticket.ticketNumber || ticket.id}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {ticket.title || ticket.subject}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {ticket.escalationLevelName || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {ticket.responsibleRoleName || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {ticket.assignedAgent || 'Unassigned'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(ticket.status)}`}>
                            {ticket.status.toUpperCase()}
                          </span>
                          {isEscalated && (
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`text-sm ${slaOverdue ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                          {slaLabel}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(ticket.escalationDate || ticket.updatedAt)}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatTime(ticket.escalationDate || ticket.updatedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex items-center gap-2"
                            onClick={() => handleEditTicket(ticket.id)}
                          >
                            <Pen className="w-4 h-4" />
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex items-center gap-2"
                            onClick={() => handleViewTicket(ticket.id)}
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {paginationData && paginationData.totalPages > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{currentPage * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min((currentPage + 1) * pageSize, paginationData.totalElements)}</span> of{' '}
                    <span className="font-medium">{paginationData.totalElements}</span> results
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
                    disabled={currentPage === 0 || paginationData.first}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
                    let pageNum;
                    if (paginationData.totalPages <= 5) {
                      pageNum = i;
                    } else if (currentPage < 3) {
                      pageNum = i;
                    } else if (currentPage > paginationData.totalPages - 4) {
                      pageNum = paginationData.totalPages - 5 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm border rounded-md ${
                          pageNum === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, paginationData.totalPages - 1))}
                    disabled={currentPage >= paginationData.totalPages - 1 || paginationData.last}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredTickets.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No escalated tickets found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm || priorityFilter !== 'all'
                ? 'Try adjusting your search criteria'
                : emptyStateMessage
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EscalatedTicketsTable;

